import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {SourceExternalReferenceUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    SourceExternalReference,
    SourceExternalReferenceOnServerCreated,
    selectSourceExternalReferencesActionLoading,
    selectSourceExternalReferenceById,
    selectLastCreatedSourceExternalReferenceId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-source-external-reference-edit',
    templateUrl: './source-external-reference-edit.component.html',
    styleUrls: ['./source-external-reference-edit.component.scss'],
})


export class SourceExternalReferenceEditComponent implements OnInit, OnDestroy {

    model: SourceExternalReference;
    oldModel: SourceExternalReference;

    sourceExternalReferenceForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    readonly = false
    /**
     * Component constructor
     *
     * @param activatedRoute
     * @param router
     * @param fb: FormBuilder
     * @param location: Location
     * @param subheaderService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private subheaderService: SubheaderService,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectSourceExternalReferencesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly 
            if (id && id > 0) {
                this.store.pipe(select(selectSourceExternalReferenceById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initClient();
                    }
                });
            } else {
                this.model = new SourceExternalReference();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initClient();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Source and External References', page: `../admin-management/linked-tables/source-of-external-references`},
            {title: this.getComponentTitle(), page: id > 0 ? `../admin-management/linked-tables/source-of-external-references/edit/${id}`
                    : `../admin-management/linked-tables/source-of-external-references/add`}
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initClient() {
        this.createForm();
    }


    createForm() {
        this.sourceExternalReferenceForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('SOURCE_EXTERNAL_REFERENCE.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('SOURCE_EXTERNAL_REFERENCE.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.sourceExternalReferenceForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.sourceExternalReferenceForm.markAsPristine();
        this.sourceExternalReferenceForm.markAsUntouched();
        this.sourceExternalReferenceForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.sourceExternalReferenceForm.controls;
        /** check form */
        if (this.sourceExternalReferenceForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateClient(this.model);
        } else {
            this.createClient(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: SourceExternalReferenceModel
     */
    updateClient(_model: SourceExternalReference) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateClient: Update<SourceExternalReference> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new SourceExternalReferenceUpdated({
            partialItem: updateClient,
            item: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: SourceExternalReferenceModel
     */
    createClient(_model: SourceExternalReference) {
        this.store.dispatch(new SourceExternalReferenceOnServerCreated({item: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedSourceExternalReferenceId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }
}
